import React from 'react';
import styled from 'styled-components';
import ReportDataProvider from '../reportDataProvider';
import TickerBanner from '../../../../components/tickerBanner';
import ReportHeaderSection from '../headerSection';
import ReportAppliedFiltersSection from '../appliedFiltersSection';
import ReportSummarySection from '../summarySection';
import ReportTableSection from '../tableSection';
import ReportPaginationSection from '../paginationSection';
import ScrollToTop from '../ScrollToTop';
import ReportLoadingFallback from '../reportLoadingFallback';
import MarketingLockedOverlay from '../marketingLockedOverlay';
import useReportDataContext from '../../hooks/useReportDataContext';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../state/store';

interface CompanyState {
  company_details: {
    company_id: number;
    maintenance_mode: {
      enabled: boolean;
      message: string;
    };
  } | null;
}

const ReportLayoutContent = () => {
  const { marketingHtmlUrl } = useReportDataContext();
  const { company_details } = useSelector((state: RootState) => state.company as CompanyState);
  const isMaintenance = company_details?.maintenance_mode?.enabled;

  if (marketingHtmlUrl) {
    return (
      <>
        <StickyHeader isMaintenance={isMaintenance ?? false}>
          <TickerBanner />
          <ReportHeaderSection />
        </StickyHeader>
        <Wrapper isMaintenance={isMaintenance ?? false}>
          <MarketingLockedOverlay url={marketingHtmlUrl} />
        </Wrapper>
      </>
    );
  }

  return (
    <>
      <StickyHeader isMaintenance={isMaintenance ?? false}>
        <TickerBanner />
        <ReportHeaderSection />
      </StickyHeader>
      <Wrapper isMaintenance={isMaintenance ?? false}>
        <ReportAppliedFiltersSection />
        <ReportSummarySection />
        <ReportTableSection />
        <ReportPaginationSection />
        <ScrollToTop />
      </Wrapper>
    </>
  );
};

const ReportLayout = () => {
  return (
    <ReportDataProvider>
      <ReportLoadingFallback>
        <ReportLayoutContent />
      </ReportLoadingFallback>
    </ReportDataProvider>
  );
};

export default ReportLayout;

const StickyHeader = styled.div<{ isMaintenance: boolean }>`
  background: ${({ theme }) => theme.body};
  position: sticky;
  top: ${({ isMaintenance }) => (isMaintenance ? "120px" : "60px")};
  z-index: 1000;
  width: 100%;
  padding: 0px 50px 20px 50px;
  font-size: 16px;

  /* Override font sizes for all child elements */
  * {
    font-size: inherit;
  }

  @media (max-width: 1023px) {
    padding: 0px 20px 15px 20px;
    top: 60px;
  }

  @media (max-width: 719px) {
    padding: 0px 10px 10px 10px;
    font-size: 14px;
  }
`;

const Wrapper = styled.div<{ isMaintenance: boolean }>`
  height: 100%;
  width: 100%;
  padding: 20px 50px;
  padding-top: ${({ isMaintenance }) => (isMaintenance ? '60px' : '0')};
  font-size: 16px;

  /* Override font sizes for all child elements */
  * {
    font-size: inherit;
  }

  /* Specific overrides for common elements */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: 1.25em; /* 20px relative to 16px base */
  }

  p,
  span,
  div,
  td,
  th,
  button,
  label,
  input {
    font-size: 16px;
  }

  small {
    font-size: 14px;
  }

  @media (max-width: 1023px) {
    padding: 15px 20px;
  }

  @media (max-width: 719px) {
    padding: 10px;
    font-size: 14px;

    p, span, div, td, th, button, label, input {
      font-size: 14px;
    }

    h1, h2, h3, h4, h5, h6 {
      font-size: 1.15em;
    }
  }
`;
